import { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription, AlertTitle } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertCircle, Save, Swords } from 'lucide-react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import type { EnemyInput, EnemyResult, EnemyLockedSections } from '@creanizer/types'
import { EnemyForm } from './EnemyForm'
import { EnemyResult as EnemyResultComponent } from './EnemyResult'
import { SavedEnemiesList } from './SavedEnemiesList'

export function EnemyGenerator() {
  const [input, setInput] = useState<EnemyInput>({
    mode: 'standard',
    threatLevel: 'C',
    difficultyLevel: 5,
    creativityLevel: 7
  })
  const [result, setResult] = useState<EnemyResult | EnemyResult[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('generate')
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  const [partialRecovery, setPartialRecovery] = useState<boolean>(false)
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null)
  const [partialRegenerateMode, setPartialRegenerateMode] = useState<boolean>(false)
  const [lockedSections, setLockedSections] = useState<EnemyLockedSections>({})
  const [existingResult, setExistingResult] = useState<EnemyResult | null>(null)

  const { isApiKeySet } = useSettingsStore()
  const { addEnemy, enemies } = useEnemyStore()
  const generator = useGenerator('enemy')
  const streamGenerator = useStreamGenerator('enemy')

  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定画面からGemini APIキーを設定してください。')
      return
    }

    setError(null)
    setResult(null)
    setBatchProgress(null)
    setPartialRecovery(false)
    
    // 部分再生成モードの場合は、既存の結果とロックセクションを入力に含める
    const finalInput = partialRegenerateMode && existingResult 
      ? { 
          ...input, 
          existingResult, 
          lockedSections 
        }
      : input
    
    try {
      // バッチモードで5体以上の場合はチャンク処理
      if (finalInput.mode === 'batch' && (finalInput.numberOfEnemies || 3) > 5) {
        const totalEnemies = finalInput.numberOfEnemies || 3
        const chunkSize = 5
        const chunks = Math.ceil(totalEnemies / chunkSize)
        const allResults: EnemyResult[] = []
        
        for (let i = 0; i < chunks; i++) {
          const currentChunkSize = Math.min(chunkSize, totalEnemies - (i * chunkSize))
          const currentChunkInput = {
            ...finalInput,
            numberOfEnemies: currentChunkSize
          }
          
          setBatchProgress({ current: i * chunkSize + 1, total: totalEnemies })
          setError(`生成中... (${i * chunkSize + 1}-${Math.min((i + 1) * chunkSize, totalEnemies)}/${totalEnemies}体)`)
          
          try {
            if (useStreaming) {
              await new Promise<void>((resolve, reject) => {
                streamGenerator.generateStream(currentChunkInput, {
                  onChunk: (chunk) => {
                    console.log(`Chunk ${i + 1}/${chunks} - Received:`, chunk.length)
                  },
                  onProgress: (bytes) => {
                    console.log(`Chunk ${i + 1}/${chunks} - Progress:`, bytes)
                  },
                  onComplete: (resultData) => {
                    if (resultData !== null && resultData !== undefined) {
                      const rawResults = Array.isArray(resultData) ? resultData : [resultData]
                      const validResults = rawResults.filter(enemy => 
                        enemy && enemy.basicInfo && enemy.basicInfo.name
                      )
                      
                      if (validResults.length > 0) {
                        allResults.push(...(validResults as EnemyResult[]))
                        setResult([...allResults])
                      }
                      resolve()
                    } else {
                      reject(new Error('チャンクの生成に失敗しました'))
                    }
                  },
                  onError: (error) => {
                    console.error(`Chunk ${i + 1}/${chunks} - Error:`, error)
                    reject(error)
                  }
                })
              })
            } else {
              const response = await generator.mutateAsync(currentChunkInput)
              const resultData = response.result
              
              if (resultData) {
                const rawResults = Array.isArray(resultData) ? resultData : [resultData]
                const validResults = rawResults.filter(enemy => 
                  enemy && enemy.basicInfo && enemy.basicInfo.name
                )
                
                if (validResults.length > 0) {
                  allResults.push(...validResults)
                  setResult([...allResults])
                }
              }
            }
            
            // 次のチャンクまで少し待機（APIレート制限対策）
            if (i < chunks - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000))
            }
          } catch (chunkError) {
            console.error(`Chunk ${i + 1} failed:`, chunkError)
            // チャンクが失敗しても、既に取得した結果は保持
            if (allResults.length > 0) {
              setError(`${allResults.length}体のみ生成できました（${totalEnemies}体中）`)
              setPartialRecovery(true)
            } else {
              throw chunkError
            }
            break
          }
        }
        
        setBatchProgress(null)
        if (allResults.length > 0) {
          setError(null)
        } else {
          setError('エネミーの生成に失敗しました')
        }
        
        return
      }
      
      // 通常の処理（5体以下または単体生成）
      if (useStreaming) {
        await streamGenerator.generateStream(finalInput, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            if (resultData !== null && resultData !== undefined) {
              if (Array.isArray(resultData) && resultData.length === 0) {
                console.error('No enemies could be recovered from incomplete data')
                setError('データの取得に失敗しました。生成数を減らすか、再度お試しください。')
                setPartialRecovery(true)
                return
              }
              
              setResult(resultData as EnemyResult | EnemyResult[])
              
              if (input.mode === 'batch' && Array.isArray(resultData)) {
                const expectedCount = input.numberOfEnemies || 5
                if (resultData.length > 0 && resultData.length < expectedCount) {
                  setPartialRecovery(true)
                  console.warn(`Partial recovery: ${resultData.length}/${expectedCount} enemies recovered`)
                  setError(`${resultData.length}体のみ取得できました（${expectedCount}体中）`)
                }
              }
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
            
            if (error.message.includes('ストリーミング')) {
              setUseStreaming(false)
              generator.mutate(input)
            }
          }
        })
      } else {
        const response = await generator.mutateAsync(finalInput)
        if (response.result) {
          setResult(response.result as EnemyResult | EnemyResult[])
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '生成中にエラーが発生しました')
    }
  }

  const handleSave = () => {
    if (!result) return

    // バッチモードの場合は複数保存
    if (Array.isArray(result)) {
      result.forEach((enemy, index) => {
        const title = `${enemy.basicInfo.name}${enemy.basicInfo.title ? ` - ${enemy.basicInfo.title}` : ''}`
        // 遅延を入れてIDの重複を防ぐ（100msに増加）
        setTimeout(() => {
          addEnemy({
            title,
            input,
            result: enemy
          })
        }, index * 100)  // 50ms -> 100msに変更
      })
    } else {
      const title = `${result.basicInfo.name}${result.basicInfo.title ? ` - ${result.basicInfo.title}` : ''}`
      addEnemy({
        title,
        input,
        result
      })
    }
  }

  const handleLoadSaved = (enemy: { input: EnemyInput; result: EnemyResult }) => {
    setInput(enemy.input)
    setResult(enemy.result)
    setExistingResult(enemy.result)
    setPartialRegenerateMode(true)
    // 初期状態：変更不可の項目のみロック、他は全てアンロック
    setLockedSections({
      basicInfo: {
        name: true,
        type: true,
        category: true,
        threat: true
      }
    })
    setActiveTab('generate')
  }

  const handlePartialRegenerate = (enemy: { input: EnemyInput; result: EnemyResult }) => {
    // 部分再生成モードを有効にする
    setPartialRegenerateMode(true)
    setExistingResult(enemy.result)
    setInput(enemy.input)
    // 初期状態：変更不可の項目のみロック
    setLockedSections({
      basicInfo: {
        name: true,
        type: true,
        category: true,
        threat: true
      }
    })
    setActiveTab('generate')
  }

  const isSaved = (enemy: EnemyResult) => {
    if (!enemy || !enemy.basicInfo) return false
    return enemies.some(saved => 
      saved?.result?.basicInfo?.name === enemy.basicInfo?.name &&
      saved?.result?.basicInfo?.threat === enemy.basicInfo?.threat
    )
  }

  const handleSaveIndividual = (enemy: EnemyResult) => {
    if (!enemy || !enemy.basicInfo) {
      console.error('Invalid enemy data:', enemy)
      return
    }
    const title = `${enemy.basicInfo.name}${enemy.basicInfo.title ? ` - ${enemy.basicInfo.title}` : ''}`
    addEnemy({
      title,
      input,
      result: enemy
    })
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Swords className="h-5 w-5" />
          <h2 className="text-xl font-semibold">エネミー・怪異ジェネレータ</h2>
        </div>
        <p className="text-muted-foreground mb-4">
          RPGの敵キャラクターから都市伝説の怪異まで、多様な敵対存在を生成します
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="generate">生成</TabsTrigger>
          <TabsTrigger value="saved">
            保存済み ({enemies.length}/500)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">生成設定</h3>
            <EnemyForm
              input={input}
              onChange={setInput}
              onGenerate={handleGenerate}
              isGenerating={generator.isPending || streamGenerator.isStreaming}
              useStreaming={useStreaming}
              onStreamingChange={setUseStreaming}
              partialRegenerateMode={partialRegenerateMode}
              existingResult={existingResult}
              lockedSections={lockedSections}
              onLockedSectionsChange={setLockedSections}
              onPartialRegenerateModeChange={setPartialRegenerateMode}
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>エラー</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {partialRecovery && result && Array.isArray(result) && (
            <Alert className="border-orange-500 bg-orange-50">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <AlertTitle className="text-orange-900">部分的な結果</AlertTitle>
              <AlertDescription className="text-orange-800">
                トークン数の制限により、{input.numberOfEnemies || 5}体中{result.length}体のエネミーのみが生成されました。
                生成数を減らすか、Proモードを使用することをお勧めします。
              </AlertDescription>
            </Alert>
          )}

          {(streamGenerator.isStreaming || batchProgress) && (
            <div className="card">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>生成中...</span>
                  <span>{Math.round(streamGenerator.progress)}%</span>
                </div>
                <Progress value={streamGenerator.progress} />
                {streamGenerator.partialContent && (
                  <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {streamGenerator.partialContent.slice(-500)}
                      {streamGenerator.partialContent.length > 500 && '...'}
                    </pre>
                  </div>
                )}
                {input.mode === 'batch' && (
                  <div className="mt-2 space-y-1">
                    <p className="text-sm text-muted-foreground">
                      バッチモードで{input.numberOfEnemies || 3}体のエネミーを生成中...
                    </p>
                    {batchProgress && (
                      <p className="text-sm font-medium">
                        進捗: {batchProgress.current}/{batchProgress.total}体
                      </p>
                    )}
                    {(input.numberOfEnemies || 3) > 5 && (
                      <p className="text-xs text-muted-foreground">
                        ※ {Math.ceil((input.numberOfEnemies || 3) / 5)}回のAPIを使用して生成します
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {result && (
            <div className="card">
              <div className="flex flex-row items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  生成結果
                  {Array.isArray(result) && ` (${result.length}体)`}
                </h3>
                {Array.isArray(result) && result.length > 1 && (
                  <button
                    onClick={handleSave}
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    すべて保存
                  </button>
                )}
              </div>
              <div>
                {Array.isArray(result) ? (
                  <Tabs defaultValue="0">
                    <TabsList className="mb-4">
                      {result.filter(enemy => enemy && enemy.basicInfo).map((enemy, index) => (
                        <TabsTrigger key={index} value={index.toString()}>
                          {enemy?.basicInfo?.name || `エネミー${index + 1}`}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    {result.filter(enemy => enemy && enemy.basicInfo).map((enemy, index) => (
                      <TabsContent key={index} value={index.toString()}>
                        <EnemyResultComponent 
                          result={enemy} 
                          onSave={() => handleSaveIndividual(enemy)}
                          isSaved={enemy ? isSaved(enemy) : false}
                          onEdit={(updatedResult) => {
                            if (Array.isArray(result)) {
                              const updatedResults = [...result]
                              updatedResults[index] = { ...enemy, ...updatedResult }
                              setResult(updatedResults)
                            }
                          }}
                        />
                      </TabsContent>
                    ))}
                  </Tabs>
                ) : result && result.basicInfo ? (
                  <EnemyResultComponent 
                    result={result} 
                    onSave={() => handleSaveIndividual(result)}
                    isSaved={isSaved(result)}
                    onEdit={(updatedResult) => {
                      setResult({ ...result, ...updatedResult })
                    }}
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    生成結果が不正です。再度生成してください。
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="saved">
          <SavedEnemiesList 
            onLoad={handleLoadSaved} 
            onPartialRegenerate={handlePartialRegenerate}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}